import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import { formatToColombianPesos } from '../../utilities/FormatFunctions';

const initialValues = {
  name: '',
  description: '',
  coverImage: null,
  images: [],
  city: '',
  sector: '',
  floor: '',
  price: '',
  area: '',
  bedrooms: '',
  realEstateTypeId: '',
  bathrooms: '',
  kitchens: '',
  yards: '',
  balcony: '',
  garage: '',
  state: '',
  businessType: '',
};

export default function RealEstatesForm({
  isEdit,
  initialData = {},
  handleOnSubmit,
  toggleModal,
  realEstateTypes,
}) {
  const [formattedValue, setFormattedValue] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');

  useEffect(() => {
    if (Object.keys(initialData).length !== 0) {
      setFormattedValue(formatToColombianPesos(initialData.price.toString()));
    }
  }, [initialData]);

  const validate = (values) => {
    const errors = {};
    if (!values.name) {
      errors.name = 'Required';
    }
    if (!values.description) {
      errors.description = 'Required';
    }
    if (!values.city) {
      errors.city = 'Required';
    }
    if (!values.sector) {
      errors.sector = 'Required';
    }
    if (!values.floor) {
      errors.floor = 'Required';
    }
    if (!values.realEstateTypeId) {
      errors.realEstateTypeId = 'Required';
    }
    if ((!values.images || values.images.length === 0) && !isEdit)
      errors.images = 'Required';
    if (!values.coverImage && !isEdit) {
      errors.coverImage = 'Required';
    }
    if (!values.price) {
      errors.price = 'Required';
    }
    if (values.area === '' || values.area < 0) {
      errors.area = 'Required';
    }
    if (values.bedrooms === '' || values.bedrooms < 0) {
      errors.bedrooms = 'Required';
    }
    if (values.bathrooms === '' || values.bathrooms < 0) {
      errors.bathrooms = 'Required';
    }
    if (values.kitchens === '' || values.kitchens < 0) {
      errors.kitchens = 'Required';
    }
    if (values.yards === '' || values.yards < 0) {
      errors.yards = 'Required';
    }
    if (!values.balcony) {
      errors.balcony = 'Required';
    }
    if (!values.garage) {
      errors.garage = 'Required';
    }
    if (!values.state) {
      errors.state = 'Required';
    }
    if (!values.businessType) {
      errors.businessType = 'Required';
    }
    return errors;
  };

  const updateBoolValues = (value) => {
    return value === 'true' ? true : false;
  };

  const updatedFormValues = (formValues) => {
    return {
      ...formValues,
      balcony: updateBoolValues(formValues.balcony),
      garage: updateBoolValues(formValues.garage),
      // Review if we need the relationship between RealEstates and Tenants.
      tenantId: isEdit
        ? formValues.tenantId
        : '0ae9b201-d2b2-4a53-32be-08de742f5f50',
    };
  };

  const handleSubmit = (values) => {
    handleOnSubmit(updatedFormValues(values));
    toggleModal();
  };

  return (
    <div className="w-full px-3 mb-2 md:mb-0">
      <Formik
        initialValues={
          Object.keys(initialData).length === 0
            ? initialValues
            : {
                ...initialData,
                balcony: initialData.balcony === true ? 'true' : 'false',
                garage: initialData.garage === true ? 'true' : 'false',
                coverImage: initialData.coverImage || null,
                images: initialData.images || [],
              }
        }
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          isValid,
          dirty,
          setFieldTouched,
          setFieldValue,
        }) => (
          <Form className="w-full space-y-4">
            <div className="flex flex-wrap -mx-3">
              <div
                className={`w-full px-3 mb-2 ${isEdit ? '' : 'lg:w-1/2 lg:mb-0'}`}
              >
                <div className="mb-4">
                  <label
                    htmlFor="name"
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  >
                    Nombre del inmueble
                  </label>
                  <Field
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="off"
                    placeholder="Apto - Código A04"
                    autoCorrect="off"
                    spellCheck="false"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                      errors.name && touched.name
                        ? 'border-red-500 placeholder-errorPlaceholder'
                        : 'border-gray-300'
                    } focus:placeholder-gray-500`}
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="description"
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  >
                    Descripción
                  </label>
                  <Field
                    as="textarea"
                    id="description"
                    name="description"
                    autoComplete="off"
                    autoCorrect="off"
                    // Review if it is needed this validation
                    spellCheck="false"
                    placeholder="Añade una breve descripción del inmueble"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                      errors.description && touched.description
                        ? 'border-red-500 placeholder-errorPlaceholder'
                        : 'border-gray-300'
                    } focus:placeholder-gray-500`}
                    rows="5"
                  />
                </div>
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="city"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Ciudad
                    </label>
                    <Field
                      as="select"
                      id="city"
                      name="city"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.city && touched.city
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      <option value="La Ceja">La Ceja</option>
                      <option value="Rionegro">Rionegro</option>
                      <option value="El Carmen">El Carmen</option>
                      <option value="Guarne">Guarne</option>
                      <option value="El Retiro">El Retiro</option>
                      {/* Add more options as needed */}
                    </Field>
                  </div>
                  <div>
                    <label
                      htmlFor="sector"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Sector
                    </label>
                    <Field
                      id="sector"
                      name="sector"
                      type="text"
                      autoComplete="off"
                      placeholder="Ubicación del inmueble"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.sector && touched.sector
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                </div>
                <div className="mb-0 grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="floor"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Piso
                    </label>
                    <Field
                      id="floor"
                      name="floor"
                      type="text"
                      autoComplete="off"
                      placeholder="Piso del inmueble"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.floor && touched.floor
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="realEstateTypeId"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Tipo de inmueble
                    </label>
                    <Field
                      as="select"
                      id="realEstateTypeId"
                      name="realEstateTypeId"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.realEstateTypeId && touched.realEstateTypeId
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      {realEstateTypes
                        .slice(0, -1)
                        .map((realEstateType, index) => (
                          <option key={index} value={realEstateType.id}>
                            {realEstateType.name.slice(0, -1)}
                          </option>
                        ))}
                    </Field>
                  </div>
                </div>
                <div className="mb-4 mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <label
                      htmlFor="bathrooms"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Baños
                    </label>
                    <Field
                      id="bathrooms"
                      name="bathrooms"
                      type="number"
                      autoComplete="off"
                      placeholder="0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.bathrooms && touched.bathrooms
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="kitchens"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Cocinas
                    </label>
                    <Field
                      id="kitchens"
                      name="kitchens"
                      type="number"
                      autoComplete="off"
                      placeholder="0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.kitchens && touched.kitchens
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="yards"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Patios
                    </label>
                    <Field
                      id="yards"
                      name="yards"
                      type="number"
                      autoComplete="off"
                      placeholder="0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.yards && touched.yards
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="area"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Area <span className="lowercase">(m²)</span>
                    </label>
                    <Field
                      id="area"
                      name="area"
                      type="number"
                      autoComplete="off"
                      placeholder="0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.area && touched.area
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                </div>
                <div className="mb-4 grid grid-cols-3 gap-4">
                  <div>
                    <label
                      htmlFor="price"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Precio
                    </label>
                    <Field
                      id="price"
                      name="price"
                      type="text"
                      value={formattedValue}
                      autoComplete="off"
                      placeholder="$ 0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.price && touched.price
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                      onChange={(event) => {
                        const numericValue = event.target.value.replace(
                          /\D/g,
                          ''
                        );
                        setFormattedValue(formatToColombianPesos(numericValue));
                        setFieldValue('price', parseInt(numericValue, 10));
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="bedrooms"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Habitaciones
                    </label>
                    <Field
                      id="bedrooms"
                      name="bedrooms"
                      type="number"
                      autoComplete="off"
                      placeholder="0"
                      autoCorrect="off"
                      spellCheck="false"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.bedrooms && touched.bedrooms
                          ? 'border-red-500 placeholder-errorPlaceholder'
                          : 'border-gray-300'
                      } focus:placeholder-gray-500`}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="businessType"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Tipo de negocio
                    </label>
                    <Field
                      as="select"
                      id="businessType"
                      name="businessType"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.businessType && touched.businessType
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      <option value="Venta">Venta</option>
                      <option value="Arriendo">Arriendo</option>
                    </Field>
                  </div>
                </div>
              </div>
              <div
                className={`w-full px-3 mb-2 ${isEdit ? '' : 'lg:w-1/2 lg:mb-0'}`}
              >
                <div className={`mb-4 mt-3 md:mt-0 ${isEdit ? 'hidden' : ''}`}>
                  <label
                    htmlFor="coverImage"
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  >
                    Imagen de portada
                  </label>
                  <input
                    id="coverImage"
                    name="coverImage"
                    type="file"
                    accept="image/*"
                    onClick={() => setFieldTouched('coverImage', true)}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0] || null;
                      setFieldValue('coverImage', file);

                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCoverImagePreview(reader.result);
                        };
                        reader.readAsDataURL(file);
                      } else {
                        setCoverImagePreview('');
                      }
                    }}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                      errors.coverImage && touched.coverImage
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  />
                  <div className="mt-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-700 mb-2">
                      Vista previa portada
                    </p>
                    <div className="w-full max-w-md aspect-[16/9] overflow-hidden rounded-md border-2 border-dashed border-gray-300 bg-gray-100">
                      {coverImagePreview ? (
                        <img
                          src={coverImagePreview}
                          alt="Vista previa de portada"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm font-medium">
                          Aquí se mostrará la portada
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`mb-4 mt-3 md:mt-0 ${isEdit ? 'hidden' : ''}`}>
                  <label
                    htmlFor="images"
                    className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                  >
                    Imágenes del inmueble
                  </label>
                  <input
                    id="images"
                    name="images"
                    type="file"
                    multiple
                    accept="image/*"
                    onClick={() => setFieldTouched('images', true)}
                    onChange={(event) => {
                      const files = Array.from(event.target.files); // Guardamos los archivos
                      setFieldValue('images', files);
                    }}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                      errors.images && touched.images
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  />
                </div>
                <div className="mb-4 grid grid-cols-3 gap-4">
                  <div>
                    <label
                      htmlFor="balcony"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Balcon
                    </label>
                    <Field
                      as="select"
                      id="balcony"
                      name="balcony"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.balcony && touched.balcony
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      <option value="true">Si</option>
                      <option value="false">No</option>
                    </Field>
                  </div>
                  <div>
                    <label
                      htmlFor="garage"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Garaje
                    </label>
                    <Field
                      as="select"
                      id="garage"
                      name="garage"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.garage && touched.garage
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      <option value="true">Si</option>
                      <option value="false">No</option>
                    </Field>
                  </div>
                  <div>
                    <label
                      htmlFor="state"
                      className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                    >
                      Estado
                    </label>
                    <Field
                      as="select"
                      id="state"
                      name="state"
                      autoComplete="off"
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-customBlue focus:border-customBlue sm:text-sm ${
                        errors.state && touched.state
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    >
                      <option value="" hidden></option>
                      <option value="Nuevo">Nuevo</option>
                      <option value="Usado">Usado</option>
                    </Field>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center pt-4 md:pt-3 pb-4 md:pb-0 border-t sm:pt-5">
              <button
                type="submit"
                className={`my-8 relative transition-all ease-in-out duration-300 shadow-[0px_10px_20px_rgba(0,0,0,0.2)] py-2 px-14 bg-[rgb(0,107,179)] rounded-full flex items-center justify-center cursor-pointer text-white gap-2 font-bold border-3 border-[#ffffff4d] outline-none overflow-hidden text-[15px] hover:scale-105 hover:border-[#fff9] before:absolute before:top-0 before:left-[-100px] before:w-[100px] before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/80 before:to-transparent before:opacity-60 hover:before:animate-shine ${
                  isValid && dirty ? '' : 'opacity-50 cursor-not-allowed'
                }`}
                disabled={!(isValid && dirty)}
              >
                {!isEdit ? (
                  // Imagen SVG para la acción "Guardar"
                  <svg
                    height="25px"
                    width="25px"
                    version="1.1"
                    id="Layer_1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 512 512"
                    xmlSpace="preserve"
                    fill="#ffffff"
                    className="mr-1"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        style={{ fill: '#FE834D' }}
                        d="M416.681,84.426H384l-177.021,32.681v65.362h242.383v-65.362C449.362,99.132,434.655,84.426,416.681,84.426z"
                      ></path>
                      <path
                        style={{ fill: '#FEA680' }}
                        d="M416.681,117.106H206.979V84.426H384C401.974,84.426,416.681,99.132,416.681,117.106z"
                      ></path>
                      <path
                        style={{ fill: '#FFC1A6' }}
                        d="M416.681,149.787H384v256h32.681c17.974,0,32.681-14.706,32.681-32.681V182.468C449.362,164.494,434.655,149.787,416.681,149.787z"
                      ></path>
                      <path
                        style={{ fill: '#FFDBCC' }}
                        d="M384,149.787H228.766V84.426c0-17.974-14.706-32.681-32.681-32.681H40.851c-17.974,0-32.681,14.706-32.681,32.681v288.681c0,17.974,14.706,32.681,32.681,32.681H384c17.974,0,32.681-14.706,32.681-32.681V182.468C416.681,164.494,401.974,149.787,384,149.787z"
                      ></path>
                      <path
                        style={{ fill: '#02ACAB' }}
                        d="M394.894,242.383v217.872c60.165,0,108.936-48.771,108.936-108.936S455.059,242.383,394.894,242.383z"
                      ></path>
                      <path
                        style={{ fill: '#42C8C6' }}
                        d="M394.894,242.383c42.115,0,76.255,48.771,76.255,108.936s-34.141,108.936-76.255,108.936c-60.165,0-108.936-48.771-108.936-108.936S334.728,242.383,394.894,242.383z"
                      ></path>
                      <path
                        style={{ fill: '#FFFFFF' }}
                        d="M449.362,334.979h-38.128v-38.128c0-9.024-7.316-16.34-16.34-16.34c-9.024,0-16.34,7.316-16.34,16.34v38.128h-38.128c-9.024,0-16.34,7.316-16.34,16.34c0,9.024,7.316,16.34,16.34,16.34h38.128v38.128c0,9.024,7.316,16.34,16.34,16.34c9.024,0,16.34-7.316,16.34-16.34V367.66h38.128c9.024,0,16.34-7.316,16.34-16.34C465.702,342.295,458.386,334.979,449.362,334.979z"
                      ></path>
                      <path d="M264.177,397.617H40.851c-13.515,0-24.511-10.996-24.511-24.511V84.426c0-13.515,10.996-24.511,24.511-24.511h155.234c13.515,0,24.511,10.996,24.511,24.511v65.362c0,4.512,3.657,8.17,8.17,8.17h187.915c13.515,0,24.511,10.996,24.511,24.511v38.134c0,4.512,3.657,8.17,8.17,8.17c4.513,0,8.17-3.658,8.17-8.17V117.106c0-22.525-18.325-40.851-40.851-40.851H261.447c-4.513,0-8.17,3.658-8.17,8.17s3.657,8.17,8.17,8.17h155.234c13.515,0,24.511,10.996,24.511,24.511v32.703c-6.832-5.141-15.321-8.192-24.511-8.192H236.936V84.426c0-22.525-18.325-40.851-40.851-40.851H40.851C18.325,43.574,0,61.901,0,84.426v288.681c0,22.525,18.325,40.851,40.851,40.851h223.326c4.513,0,8.17-3.658,8.17-8.17C272.347,401.275,268.689,397.617,264.177,397.617z"></path>
                      <path d="M394.894,234.213c-64.572,0-117.106,52.533-117.106,117.106s52.534,117.106,117.106,117.106S512,415.892,512,351.319S459.466,234.213,394.894,234.213z M394.894,452.085c-55.563,0-100.766-45.203-100.766-100.766s45.203-100.766,100.766-100.766S495.66,295.756,495.66,351.319S450.457,452.085,394.894,452.085z"></path>
                      <path d="M449.362,326.809h-29.957v-29.957c0-13.515-10.996-24.511-24.511-24.511c-13.515,0-24.511,10.996-24.511,24.511v29.957h-29.957c-13.515,0-24.511,10.996-24.511,24.511c0,13.515,10.996,24.511,24.511,24.511h29.957v29.957c0,13.515,10.996,24.511,24.511,24.511c13.515,0,24.511-10.996,24.511-24.511V375.83h29.957c13.515,0,24.511-10.996,24.511-24.511C473.872,337.805,462.876,326.809,449.362,326.809z M449.362,351.319h-38.128c-4.513,0-8.17,3.658-8.17,8.17v38.128c0,4.512-3.657,8.17-8.17,8.17s-8.17-3.658-8.17-8.17v-38.128c0-4.512-3.657-8.17-8.17-8.17h-38.128c-4.513,0-8.17-3.658-8.17-8.17c0-4.512,3.657-8.17,8.17-8.17h38.128c4.513,0,8.17-3.658,8.17-8.17v-38.128c0-4.512,3.657-8.17,8.17-8.17s8.17,3.658,8.17,8.17v38.128c0,4.512,3.657,8.17,8.17,8.17h38.128c4.513,0,8.17,3.658,8.17,8.17C457.532,347.662,453.875,351.319,449.362,351.319z"></path>
                      <path d="M131.915,213.617c0-4.512-3.658-8.17-8.17-8.17H81.702c-4.513,0-8.17,3.658-8.17,8.17s3.658,8.17,8.17,8.17h42.043C128.258,221.787,131.915,218.129,131.915,213.617z"></path>
                      <path d="M211.149,213.617c0-4.512-3.658-8.17-8.17-8.17h-93.36c-4.513,0-8.17,3.658-8.17,8.17s3.658,8.17,8.17,8.17h93.36C207.492,221.787,211.149,218.129,211.149,213.617z"></path>
                      <path d="M307.064,237.128h-189.02c-4.513,0-8.17,3.658-8.17,8.17c0,4.512,3.658,8.17,8.17,8.17h189.02c4.513,0,8.17-3.658,8.17-8.17C315.234,240.786,311.577,237.128,307.064,237.128z"></path>
                      <path d="M307.064,285.617h-189.02c-4.513,0-8.17,3.658-8.17,8.17c0,4.512,3.658,8.17,8.17,8.17h189.02c4.513,0,8.17-3.658,8.17-8.17C315.234,289.274,311.577,285.617,307.064,285.617z"></path>
                      <path d="M307.064,334.106h-189.02c-4.513,0-8.17,3.658-8.17,8.17c0,4.513,3.658,8.17,8.17,8.17h189.02c4.513,0,8.17-3.657,8.17-8.17C315.234,337.764,311.577,334.106,307.064,334.106z"></path>
                      <path d="M307.064,382.596h-189.02c-4.513,0-8.17,3.658-8.17,8.17s3.658,8.17,8.17,8.17h189.02c4.513,0,8.17-3.658,8.17-8.17S311.577,382.596,307.064,382.596z"></path>
                      <path d="M139.383,352.276c-4.513,0-8.17,3.658-8.17,8.17v30.298c0,4.512,3.658,8.17,8.17,8.17s8.17-3.658,8.17-8.17v-30.298C147.553,355.934,143.896,352.276,139.383,352.276z"></path>
                      <path d="M177.149,328.17c-4.513,0-8.17,3.658-8.17,8.17v54.404c0,4.512,3.658,8.17,8.17,8.17s8.17-3.658,8.17-8.17V336.34C185.319,331.828,181.662,328.17,177.149,328.17z"></path>
                      <path d="M214.915,352.276c-4.513,0-8.17,3.658-8.17,8.17v30.298c0,4.512,3.658,8.17,8.17,8.17s8.17-3.658,8.17-8.17v-30.298C223.085,355.934,219.428,352.276,214.915,352.276z"></path>
                    </g>
                  </svg>
                ) : (
                  <svg
                    height="25px"
                    width="25px"
                    version="1.1"
                    id="Layer_1"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    fill="#000000"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      <path
                        style={{ fill: '#FE834D' }}
                        d="M416.681,84.426H384l-177.021,32.681v65.362h242.383v-65.362 C449.362,99.132,434.655,84.426,416.681,84.426z"
                      />
                      <path
                        style={{ fill: '#FEA680' }}
                        d="M416.681,117.106H206.979V84.426H384C401.974,84.426,416.681,99.132,416.681,117.106z"
                      />
                      <path
                        style={{ fill: '#FFC1A6' }}
                        d="M416.681,149.787H384v256h32.681c17.974,0,32.681-14.706,32.681-32.681V182.468 C449.362,164.494,434.655,149.787,416.681,149.787z"
                      />
                      <path
                        style={{ fill: '#FFDBCC' }}
                        d="M384,149.787H228.766V84.426c0-17.974-14.706-32.681-32.681-32.681H40.851 c-17.974,0-32.681,14.706-32.681,32.681v288.681c0,17.974,14.706,32.681,32.681,32.681H384c17.974,0,32.681-14.706,32.681-32.681 V182.468C416.681,164.494,401.974,149.787,384,149.787z"
                      />
                      <path
                        style={{ fill: '#ABECEC' }}
                        d="M394.894,242.383v196.085l54.469,7.21c32.56-18.835,54.467-54.038,54.467-94.359 C503.83,291.154,455.059,242.383,394.894,242.383z"
                      />
                      <path
                        style={{ fill: '#D5F6F5' }}
                        d="M394.894,242.383c42.115,0,76.255,48.771,76.255,108.936c0,29.679-8.307,56.584-21.781,76.231 l-108.944,18.128c-32.559-18.836-54.467-54.039-54.467-94.359C285.957,291.154,334.728,242.383,394.894,242.383z"
                      />
                      <path
                        style={{ fill: '#FEDC3D' }}
                        d="M393.017,460.231c0.625,0.011,1.25,0.024,1.877,0.024c19.843,0,38.446-5.307,54.469-14.577 l-0.001-71.483L384,363.302L393.017,460.231z"
                      />
                      <path
                        style={{ fill: '#FFF1B1' }}
                        d="M340.426,374.196l-0.002,71.483c16.023,9.269,34.628,14.577,54.47,14.577v-96.953L340.426,374.196z"
                      />
                      <polygon
                        style={{ fill: '#FFC1A6' }}
                        points="422.128,325.174 449.362,374.196 416.681,374.196 384,314.281"
                      />
                      <polygon
                        style={{ fill: '#FFDBCC' }}
                        points="405.787,314.281 416.681,374.196 340.426,374.196 367.66,325.174"
                      />
                      <polygon
                        style={{ fill: '#737373' }}
                        points="367.66,325.174 422.128,325.174 394.894,276.153"
                      />
                      <path d="M264.177,397.617H40.851c-13.516,0-24.511-10.996-24.511-24.511V84.426c0-13.515,10.995-24.511,24.511-24.511h155.234 c13.516,0,24.511,10.996,24.511,24.511v65.362c0,4.512,3.658,8.17,8.17,8.17h187.915c13.516,0,24.511,10.996,24.511,24.511v38.134 c0,4.512,3.658,8.17,8.17,8.17c4.512,0,8.17-3.658,8.17-8.17V117.106c0-22.525-18.325-40.851-40.851-40.851H261.447 c-4.512,0-8.17,3.658-8.17,8.17s3.658,8.17,8.17,8.17h155.234c13.516,0,24.511,10.996,24.511,24.511v32.703 c-6.832-5.141-15.321-8.192-24.511-8.192H236.936V84.426c0-22.525-18.325-40.851-40.851-40.851H40.851 C18.325,43.574,0,61.901,0,84.426v288.681c0,22.525,18.325,40.851,40.851,40.851h223.326c4.512,0,8.17-3.658,8.17-8.17 C272.347,401.275,268.689,397.617,264.177,397.617z"></path>
                      <circle cx="106.213" cy="149.787" r="8.17"></circle>
                      <circle cx="73.532" cy="117.106" r="8.17"></circle>
                      <path d="M394.894,234.213c-64.573,0-117.106,52.533-117.106,117.106s52.533,117.106,117.106,117.106S512,415.892,512,351.319 S459.467,234.213,394.894,234.213z M372.467,333.345h44.853l18.156,32.681h-81.165L372.467,333.345z M381.545,317.004l13.349-24.028 l13.349,24.028H381.545z M403.064,382.366h38.128l0.001,58.433c-11.584,6.019-24.476,9.852-38.129,10.952V382.366z M348.596,382.366 h38.128v69.387c-13.653-1.1-26.546-4.935-38.13-10.954L348.596,382.366z M457.533,430.191l-0.001-55.995 c0-1.389-0.354-2.754-1.028-3.967l-27.184-48.93c-0.024-0.042-27.284-49.113-27.284-49.113c-1.441-2.594-4.176-4.203-7.142-4.203 c-2.966,0-5.701,1.609-7.142,4.203l-27.261,49.079c-0.001,0.002-27.207,48.964-27.207,48.964c-0.674,1.212-1.027,2.576-1.027,3.967 l-0.002,55.996c-13.472-19.65-21.775-46.556-21.775-76.23C318.638,289.38,353.758,242.383,394.894,242.383 s76.255,46.997,76.255,108.936C479.308,383.635,471.006,410.541,457.533,430.191z"></path>
                    </g>
                  </svg>
                )}
                {isEdit ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
